/* TimeCapsuleModal — söndags-summering av veckans gillade Tidsfeed-klipp.
 * Klicka "📼 Veckans Tidskapsel" i Tidsfeed-headern. */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Film, Sparkles, ChevronLeft, ChevronRight, History } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TimeCapsuleModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [clipIdx, setClipIdx] = useState(0);
  const [historyIdx, setHistoryIdx] = useState(0); // 0 = this week

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/timefeed/timecapsule`, { withCredentials: true });
        setData(r.data);
      } catch (e) {
        toast.error('Kunde inte ladda tidskapseln');
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/timefeed/timecapsule/generate`, {}, { withCredentials: true });
      setData((d) => ({ ...d, items: [r.data, ...(d?.items || [])], has_this_week: true }));
      setHistoryIdx(0);
      setClipIdx(0);
      toast.success('📼 Tidskapsel klar!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa');
    } finally { setBusy(false); }
  };

  const capsules = data?.items || [];
  const current = capsules[historyIdx];
  const clips = current?.clips || [];
  const clip = clips[clipIdx];

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur flex items-center justify-center p-3" onClick={onClose} data-testid="timecapsule">
      <div className="w-full max-w-md bg-gradient-to-br from-amber-50 via-rose-50 to-violet-50 rounded-3xl shadow-2xl border-4 border-amber-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-amber-400 via-rose-400 to-violet-500 px-5 py-3 text-white flex justify-between items-center">
          <h3 className="font-extrabold flex items-center gap-2"><Film className="w-4 h-4" /> Veckans Tidskapsel</h3>
          <button onClick={onClose} data-testid="capsule-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-3">
          {loading ? (
            <div className="text-center py-8 text-amber-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          ) : capsules.length === 0 ? (
            <div className="text-center space-y-3 py-4">
              <div className="text-5xl">📼</div>
              <p className="text-sm text-gray-700">Du har inga tidskapslar än. Gilla några klipp i Tidsfeeden den här veckan, sedan kan du skapa söndagens kapsel här!</p>
              <button
                onClick={generate}
                disabled={busy}
                data-testid="capsule-generate"
                className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-amber-500 to-rose-500 text-white font-extrabold disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <><Sparkles className="w-4 h-4 inline mr-1" /> Skapa veckans nu</>}
              </button>
            </div>
          ) : (
            <>
              {/* Capsule history selector */}
              {capsules.length > 1 && (
                <div className="flex items-center justify-between text-xs">
                  <button
                    onClick={() => setHistoryIdx((i) => Math.min(i + 1, capsules.length - 1))}
                    disabled={historyIdx >= capsules.length - 1}
                    className="text-amber-700 disabled:opacity-30 flex items-center gap-1"
                  >
                    <History className="w-3 h-3" /> Tidigare
                  </button>
                  <span className="font-bold text-amber-900">{current?.week}</span>
                  <button
                    onClick={() => { setHistoryIdx((i) => Math.max(i - 1, 0)); setClipIdx(0); }}
                    disabled={historyIdx === 0}
                    className="text-amber-700 disabled:opacity-30"
                  >
                    Nyare →
                  </button>
                </div>
              )}

              {!data.has_this_week && historyIdx === 0 && (
                <button
                  onClick={generate}
                  disabled={busy}
                  data-testid="capsule-generate-this-week"
                  className="w-full py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : '✨ Skapa veckans tidskapsel'}
                </button>
              )}

              <h2 className="text-lg font-extrabold text-amber-900 text-center">{current?.title}</h2>

              {clip && (
                <div className="bg-white rounded-2xl border-2 border-amber-200 overflow-hidden shadow-sm" data-testid={`capsule-clip-${clipIdx}`}>
                  {clip.video_b64 ? (
                    <video src={`data:video/mp4;base64,${clip.video_b64}`} autoPlay muted loop playsInline className="w-full max-h-64 object-cover" />
                  ) : clip.image_b64 ? (
                    <img src={`data:image/png;base64,${clip.image_b64}`} alt={clip.title} className="w-full max-h-64 object-cover" />
                  ) : null}
                  <div className="p-3 space-y-1">
                    <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">
                      {clip.year} · {clip.place}
                    </div>
                    <h3 className="font-extrabold text-amber-900">{clip.title}</h3>
                    {clip.subtitle && <p className="text-xs text-gray-700">{clip.subtitle}</p>}
                  </div>
                </div>
              )}

              {clips.length > 1 && (
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setClipIdx((i) => Math.max(i - 1, 0))}
                    disabled={clipIdx === 0}
                    data-testid="capsule-prev"
                    className="p-2 rounded-full bg-white border border-amber-200 disabled:opacity-30"
                  ><ChevronLeft className="w-4 h-4 text-amber-700" /></button>
                  <span className="text-xs font-bold text-amber-700">{clipIdx + 1} / {clips.length}</span>
                  <button
                    onClick={() => setClipIdx((i) => Math.min(i + 1, clips.length - 1))}
                    disabled={clipIdx >= clips.length - 1}
                    data-testid="capsule-next"
                    className="p-2 rounded-full bg-white border border-amber-200 disabled:opacity-30"
                  ><ChevronRight className="w-4 h-4 text-amber-700" /></button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
